import { LogPayload } from '@/types/logging';

import { Severity } from '@treyd-io/core/constants/logging';
import { pick } from 'lodash';
import pino, { Logger } from 'pino';

export class LoggingService {
  private logger: Logger;

  constructor() {
    this.logger =
      process.env['NODE_ENV'] === 'production'
        ? // JSON in production
          pino({
            formatters: {
              bindings: (bindings) => ({
                pid: bindings.pid,
                host: bindings.hostname,
              }),
              level: (label) => ({
                level: label.toUpperCase(),
              }),
            },
            timestamp: pino.stdTimeFunctions.isoTime,
          })
        : // Pretty print in development
          pino({
            transport: {
              target: 'pino-pretty',
              options: {
                colorize: true,
              },
            },
            level: 'debug',
          });
  }

  private serializePayload = ({ config, ...rest }: LogPayload) => {
    return {
      ...pick(rest, ['data', 'status', 'url', 'method', 'additionalData']),
      config: pick(config, ['data', 'url']),
    };
  };

  private getLoggerFunc = (severity?: Severity) => {
    const mapper = {
      [Severity.ERROR]: (payload: unknown, msg?: string) =>
        this.logger.error(payload, msg ?? 'Error in lager request'),
      [Severity.INFO]: (payload: unknown, msg?: string) =>
        this.logger.info(payload, msg ?? 'Request in lager'),
      [Severity.WARNING]: (payload: unknown, msg?: string) =>
        this.logger.warn(payload, msg ?? 'Warning in lager request'),
    };
    return severity ? mapper[severity] : undefined;
  };

  public log = (payload: LogPayload, msg?: string) => {
    const serializedPayload = this.serializePayload(payload);
    const logFunc = this.getLoggerFunc(
      serializedPayload?.additionalData?.severity || Severity.ERROR
    );
    logFunc?.(serializedPayload, msg);
  };
}
